"use client";

/* eslint-disable react/prop-types */
import { useEffect, useState, Fragment } from "react";
import Image from "next/image";
import Link from "next/link";
import { db } from "@kkeut/lib/firebase/client";
import { getDoc, doc } from "firebase/firestore";

/* ───────── 하드코딩 정책 링크 (기본 경로) ───────── */
const POLICY_LINKS = {
  privacy: "/policy/private",
  terms: "/policy/user",
  aftercare: "/care",
  uncoveredFees: "/uncovered-fees",
};

/* 언어 접두어 붙여주는 헬퍼 */
function localePath(path, locale) {
  if (!path) return "#";
  if (/^https?:\/\//i.test(path)) return path;
  const p = path.startsWith("/") ? path : `/${path}`;
  if (/^\/(en|ja|zh|th)(?=\/|$)/i.test(p)) return p;
  return locale === "ko" ? p : `/${locale}${p}`;
}

/* 라벨 i18n */
const T = {
  companyLabel: { ko: "상호", en: "Business Name", ja: "事業者名", zh: "商号", th: "ชื่อกิจการ" },
  ceoLabel: { ko: "대표", en: "Representative", ja: "代表者", zh: "代表人", th: "ผู้แทน" },
  bizNoLabel: { ko: "사업자등록번호", en: "Business Reg. No.", ja: "事業者登録番号", zh: "营业执照号", th: "เลขทะเบียนธุรกิจ" },
  addressLineLabel: { ko: "주소", en: "Address", ja: "住所", zh: "地址", th: "ที่อยู่" },
  policy: {
    privacy: { ko: "개인정보처리방침", en: "Privacy Policy", ja: "個人情報保護方針", zh: "隐私政策", th: "นโยบายความเป็นส่วนตัว" },
    terms: { ko: "이용약관", en: "Terms of Use", ja: "利用規約", zh: "使用条款", th: "ข้อกำหนดการใช้งาน" },
    aftercare: { ko: "시술 후 주의사항", en: "Aftercare", ja: "施術後の注意事項", zh: "术后注意事项", th: "ข้อควรระวังหลังการรักษา" },
    uncoveredFees: { ko: "비급여 진료비용 안내", en: "Non-Covered Fees", ja: "保険外診療費用", zh: "非医保费用", th: "ค่ารักษาที่ไม่อยู่ในประกัน" },
  },
  allRights: { ko: "All Rights Reserved", en: "All Rights Reserved", ja: "All Rights Reserved", zh: "保留所有权利", th: "All Rights Reserved" },
};

const L = (d, locale) => d?.[locale] ?? d?.ko ?? "";

function getLocaleString(v, locale) {
  if (v == null) return "";
  if (typeof v === "string") return v;
  return v?.[locale] ?? v?.ko ?? Object.values(v || {})[0] ?? "";
}

const norm = (v, locale) => (typeof v === "string" ? v : getLocaleString(v, locale) || "");

/**
 * 데스크탑 + 모바일 공용 메인 Footer (어두운 배경).
 *
 * 사용 방식:
 *   - `branchId` 만 주면 Firestore 에서 fetch
 *   - `initialInfo` 직접 주입 (Storybook / 테스트 / SSR)
 *
 * @param {object} props
 * @param {string} [props.branchId]
 * @param {object} [props.initialInfo]
 * @param {object} [props.branchTitle] - 지점명 다국어 객체 (예: { ko: "홍대 끗 한의원", ... })
 * @param {string} [props.fallbackAddress] - Firestore 미설정 시 주소 fallback
 * @param {string} [props.locale="ko"] - 현재 로캘
 */
export function Footer({
  branchId,
  initialInfo = null,
  branchTitle = { ko: "끗 한의원" },
  fallbackAddress = "",
  locale = "ko",
}) {
  const [info, setInfo] = useState(initialInfo);

  useEffect(() => {
    if (initialInfo) return;
    if (!branchId) return;
    let cancelled = false;
    (async () => {
      try {
        const [defaultSnap, branchSnap, uncoveredSnap] = await Promise.all([
          getDoc(doc(db, branchId, "default")),
          getDoc(doc(db, "branches", branchId)),
          getDoc(doc(db, branchId, "uncoveredItems")),
        ]);
        if (cancelled) return;
        const def = defaultSnap.exists() ? defaultSnap.data() : {};
        const br = branchSnap.exists() ? branchSnap.data() : {};
        // {branchId}/uncoveredItems 도큐먼트의 enabled — 푸터 노출 여부 (관리자 페이지 토글)
        const uncoveredItems = uncoveredSnap.exists() ? uncoveredSnap.data() : null;
        setInfo({
          ...def,
          ...br,
          uncoveredItems,
          footerLogo: br?.footerLogo || br?.logoFooter || br?.logoWithTitle || br?.logo || def?.footerLogo || "/assets/withTitleLogo.png",
          companyName: def?.companyName ?? br?.companyName ?? null, // 없으면 아래에서 언어별 branchTitle (#981)
          representative: def?.representative ?? br?.representative ?? "",
          businessNumber: def?.businessNumber ?? br?.businessNumber ?? "",
          companyAddress: def?.companyAddress ?? br?.companyAddress ?? def?.address ?? br?.address ?? fallbackAddress,
          addressDetail: def?.addressDetail ?? br?.addressDetail ?? "",
        });
      } catch (e) {
        if (!cancelled) console.error("Footer fetch 실패:", e);
      }
    })();
    return () => { cancelled = true; };
  }, [branchId, initialInfo, branchTitle, fallbackAddress]);

  if (!info) return null;

  const logoSrc = norm(info?.footerLogo, locale) || "/assets/withTitleLogo.png";
  const branchTitleStr = branchTitle[locale] || branchTitle.ko || "끗 한의원";
  const company = norm(info?.companyName, locale) || branchTitleStr;

  const hrefPrivacy = localePath(POLICY_LINKS.privacy, locale);
  const hrefTerms = localePath(POLICY_LINKS.terms, locale);
  const hrefAftercare = localePath(POLICY_LINKS.aftercare, locale);
  const hrefUncoveredFees = localePath(POLICY_LINKS.uncoveredFees, locale);
  const showUncoveredFees = info?.uncoveredItems?.enabled === true;

  return (
    <section className="bg-[#242424] text-[#f0f0f0]">
      <div className="mx-auto max-w-[1200px] px-5 py-12">
        <div className="mb-3 text-center">
          <Image
            src={logoSrc}
            alt="clinic logo"
            width={468}
            height={89}
            className="mx-auto mb-3 h-auto w-[240px]"
          />
        </div>

        <div className="mb-8 flex flex-wrap items-center justify-center text-sm opacity-90">
          <LinkItem href={hrefPrivacy} label={L(T.policy.privacy, locale)} first />
          <Sep />
          <LinkItem href={hrefTerms} label={L(T.policy.terms, locale)} />
          <Sep />
          <LinkItem href={hrefAftercare} label={L(T.policy.aftercare, locale)} />
          {showUncoveredFees && (
            <>
              <Sep />
              <LinkItem href={hrefUncoveredFees} label={L(T.policy.uncoveredFees, locale)} />
            </>
          )}
        </div>

        <div className="mb-2 text-center text-[15px] leading-relaxed opacity-90">
          <p>
            {L(T.companyLabel, locale)} : {branchTitleStr}
            &nbsp;|&nbsp; {L(T.ceoLabel, locale)} : {norm(info?.representative, locale) || "-"}
            &nbsp;|&nbsp; {L(T.bizNoLabel, locale)} : {info?.businessNumber || "-"}
          </p>
          <p className="mt-1">
            {L(T.addressLineLabel, locale)} :{" "}
            {[norm(info?.companyAddress, locale), norm(info?.addressDetail, locale)]
              .filter(Boolean)
              .join(" ") || "-"}
          </p>
        </div>

        <p className="mt-8 text-center text-sm opacity-60">
          © {new Date().getFullYear()} {company}. {L(T.allRights, locale)}
        </p>
      </div>
    </section>
  );
}

function LinkItem({ href, label, first }) {
  return (
    <Fragment>
      {!first && null}
      <Link href={href} className="hover:text-white hover:underline underline-offset-4">
        {label}
      </Link>
    </Fragment>
  );
}

function Sep() {
  return <span className="mx-3 opacity-40">|</span>;
}
